import React from "react"
import Radium from "radium"
import ViewListIcon from "@material-ui/icons/ViewList"
import DescriptionIcon from "@material-ui/icons/Description"

import { ColorPalette } from "../../config/colors"
import {
  removeUnderScores,
  timestampToDateString,
  titleCase,
  toLowerCaseCustom,
  toUpperCaseCustom,
  toValidPfDate,
} from "../../utils"
import DataLogHeader from "./DataLogHeader"
import { ChangeLog, LogScreenMode, FieldChanges, DocMetaData, LogMetaData, UnixMsString } from "../../types"

interface TimelineProps {
  logData: ChangeLog
  mode: LogScreenMode
  style?: React.CSSProperties | undefined
}

const DataLogBlock: React.FC<TimelineProps> = (props: TimelineProps) => {
  const { logData, mode, style } = props
  let item = null

  function renderFieldChanges(fieldChanges: FieldChanges) {
    let categoryItems: any[] = []
    let rowItems: JSX.Element[] = []
    Object.keys(fieldChanges).forEach((label) => {
      const description = `${label}: \xa0\  ${fieldChanges[label].old} \xa0\ =>  \xa0\ ${fieldChanges[label].new}`
      const item = (
        <div style={styles.rowItemContainer} key={`category_rawData_${label}`}>
          <ViewListIcon style={styles.rowItemIcon} />
          <p style={styles.rowItemText}>{toUpperCaseCustom(removeUnderScores(description))}</p>
        </div>
      )
      rowItems.push(item)
    })

    if (rowItems.length) {
      item = (
        <div style={{ width: "100%", margin: "0 0 20px 0px" }} key={`category_${1}`}>
          {rowItems}
        </div>
      )
      categoryItems.push(item)
    }

    return categoryItems
  }

  function formatLogMetaData(logMetaData: LogMetaData) {
    // const NO_NAME_MESSAGE = "<NO NAME AVAILABLE>"
    // const NO_SURNAME_MESSAGE = "<NO SURNAME AVAILABLE>"
    const NO_NAME_MESSAGE = " - "
    const NO_SURNAME_MESSAGE = " - "

    const newLogMetaData = { ...logMetaData } as LogMetaData & { dateString: string }
    let { appVersion, userFirstName = NO_NAME_MESSAGE, userSurname = NO_SURNAME_MESSAGE, dateActioned } = logMetaData
    if (!appVersion || toUpperCaseCustom(appVersion) === "UNKNOWN") {
      newLogMetaData.appVersion = UNKNOWN_APP_VERSION
    }
    if (toLowerCaseCustom(userFirstName) === "unknown") {
      newLogMetaData.userFirstName = NO_NAME_MESSAGE
    }
    if (toLowerCaseCustom(userSurname) === "unknown") {
      newLogMetaData.userSurname = NO_SURNAME_MESSAGE
    }
    let dateString = toValidPfDate(dateActioned, "DD-MM-YYYY, HH:mm", undefined, true)
    if (dateString.includes("Invalid")) {
      dateString = " - " as UnixMsString
    }
    newLogMetaData.dateString = dateString
    return newLogMetaData
  }

  function renderDocs(data: (DocMetaData & { op?: "create" | "remove"; unixMs?: string })[]) {
    return data
      .map((itemDetail: DocMetaData & { op?: "create" | "remove"; unixMs?: string }) => {
        const { docName, op, unixMs } = itemDetail
        if (!docName) {
          return null
        }
        const removalDate = toValidPfDate(unixMs, "DD-MM-YYYY, HH:mm", undefined, true)
        const docNamePrefix = op === "remove" ? `Deleted: ` : ""
        const docNameSuffix = op === "remove" ? `${removalDate}` : ""
        return (
          <div style={styles.rowItemContainer} key={`category_docs_${docName}`}>
            <DescriptionIcon
              style={{
                ...styles.rowItemIcon,
                color: op === "remove" ? ColorPalette.WARNING_RED : ColorPalette.DARK_GREY,
              }}
            />
            <p style={styles.rowItemText}>{toUpperCaseCustom(removeUnderScores(`${docNamePrefix} ${docName}`))}</p>
          </div>
        )
      })
      .filter((item) => item)
  }
  const NO_NAME_MESSAGE = " - "
  const NO_SURNAME_MESSAGE = " - "
  const NO_ID_PASSPORT_MESSAGE = " - "
  const UNKNOWN_APP_VERSION = "-.--"

  const { logMetaData, fieldChanges, docsMetaData } = logData
  const {
    userFirstName,
    userSurname,
    appVersion,
    name = NO_NAME_MESSAGE,
    surname = NO_SURNAME_MESSAGE,
    idPassport = NO_ID_PASSPORT_MESSAGE,
    dateString,
    profilePk,
  } = formatLogMetaData(logMetaData)
  let userName = ""
  if (userFirstName || userSurname) {
    userName = `${titleCase(userFirstName)} ${titleCase(userSurname)}`
  }
  const rawDataSection = renderFieldChanges(fieldChanges)
  const docsSection = renderDocs(docsMetaData)

  if (!rawDataSection.length && !docsSection.length) {
    return null
  }
  let subTitle = `${appVersion} - ${userName} - ${dateString}`
  if (!userName) {
    subTitle = `${appVersion} - ${dateString}`
  }
  return (
    <div style={{ ...styles.container, ...style }}>
      <DataLogHeader
        mode={mode}
        title={`${name} ${surname} | ${idPassport}`}
        subTitle={subTitle}
        id={`System ID: ${profilePk}`}
      />
      {rawDataSection}
      {docsSection}
    </div>
  )
}

let styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "flex-start",
    borderRadius: 10,
    padding: "20px 20px 20px 30px",
    width: "90%",
    backgroundColor: ColorPalette.BUTTON_HOVER_GREY,
    boxShadow: "0px 2px 6px rgba(0, 0, 30, 0.2)",
    marginTop: 30,
  },
  rowItemContainer: {
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    marginLeft: window.innerWidth * 0.013,
    paddingVertical: 1,
  },
  rowItemIcon: {
    width: "1rem",
    height: "1rem",
    color: ColorPalette.DARK_GREY,
  },
  rowItemText: {
    color: ColorPalette.SECONDARY_TEXT,
    fontSize: "0.8rem",
    margin: "0 0 0 20px",
  },
  subHeaderStyle: {
    fontSize: "0.85rem",
    fontWeight: "300",
    fontFamily: "Roboto",
    color: ColorPalette.PRIMARY_TEXT,
    width: "100%",
    textAlign: "center" as "center",
  },
}

export default Radium(DataLogBlock)
